import type { Distributor, InventoryItem, Product } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

interface AdvisorsStatsProps {
  distributors: Distributor[];
  inventory: InventoryItem[];
  products: Product[];
}

const STAT_META = [
  { icon: "ri-team-line", label: "Asesores activos", bubble: "bg-primary-500/25 text-primary-100" },
  { icon: "ri-user-follow-line", label: "Con tenencia en consignación", bubble: "bg-accent-500/20 text-accent-200" },
  { icon: "ri-percent-line", label: "Margen promedio", bubble: "bg-secondary-500/25 text-secondary-200" },
  { icon: "ri-hand-coin-line", label: "Valor en consignación", bubble: "bg-accent-500/15 text-accent-100" },
];

const consignedValue = (
  advisorId: string,
  inventory: InventoryItem[],
  products: Product[]
): number =>
  inventory.reduce((acc, item) => {
    const units = item.stockByAdvisor[advisorId] ?? 0;
    const product = products.find((p) => p.id === item.productId);
    return acc + units * (product ? product.pricePublic : 0);
  }, 0);

// Resumen ejecutivo de la red de asesores.
const AdvisorsStats = ({ distributors, inventory, products }: AdvisorsStatsProps) => {
  const withStock = distributors.filter((d) =>
    inventory.some((i) => (i.stockByAdvisor[d.id] ?? 0) > 0)
  ).length;
  const avgMargin = distributors.length
    ? Math.round(distributors.reduce((acc, d) => acc + d.margin, 0) / distributors.length)
    : 0;
  const totalConsigned = distributors.reduce(
    (acc, d) => acc + consignedValue(d.id, inventory, products),
    0
  );

  const values = [
    formatNumber(distributors.length),
    formatNumber(withStock),
    `${avgMargin}%`,
    formatCOP(totalConsigned),
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
      {STAT_META.map((meta, i) => (
        <div key={meta.label} className="glass-panel rounded-lg p-4 animate-fade-up">
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.bubble}`}>
            <i className={`${meta.icon} text-lg`} />
          </span>
          <p className="text-[11px] text-foreground-500 mt-3">{meta.label}</p>
          <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
            {values[i]}
          </p>
        </div>
      ))}
    </div>
  );
};

export { consignedValue };
export default AdvisorsStats;